/** Ports of get-amount-by-currency.ts (Vue). */

const currencyNames = new Intl.DisplayNames(["fr"], { type: "currency" });

/** French name of an ISO 4217 code ("USD" → "dollar des États-Unis"), the code itself when unknown. */
export function currencyDisplayName(code: string): string {
  try {
    return currencyNames.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Mirrors ui/inputs/Price.vue currencies. */
export const PRICE_CURRENCIES = [
  "AED",
  "AFN",
  "ALL",
  "AMD",
  "ANG",
  "AOA",
  "ARS",
  "AUD",
  "AWG",
  "AZN",
  "BAM",
  "BBD",
  "BDT",
  "BGN",
  "BHD",
  "BIF",
  "BMD",
  "BND",
  "BOB",
  "BOV",
  "BRL",
  "BSD",
  "BTN",
  "BWP",
  "BZD",
  "CAD",
  "CDF",
  "CHF",
  "CLF",
  "CLP",
  "CNY",
  "COP",
  "COU",
  "CRC",
  "CUC",
  "CUP",
  "CVE",
  "CZK",
  "DJF",
  "DKK",
  "DOP",
  "DZD",
  "EGP",
  "ERN",
  "ETB",
  "EUR",
  "FJD",
  "FKP",
  "GBP",
  "GEL",
  "GHS",
  "GIP",
  "GMD",
  "GNF",
  "GTQ",
  "GYD",
  "HKD",
  "HNL",
  "HRK",
  "HTG",
  "HUF",
  "IDR",
  "ILS",
  "INR",
  "IQD",
  "IRR",
  "ISK",
  "JMD",
  "JOD",
  "JPY",
  "KES",
  "KGS",
  "KHR",
  "KMF",
  "KPW",
  "KRW",
  "KWD",
  "KYD",
  "KZT",
  "LAK",
  "LBP",
  "LKR",
  "LRD",
  "LSL",
  "LYD",
  "MAD",
  "MDL",
  "MGA",
  "MKD",
  "MMK",
  "MNT",
  "MOP",
  "MUR",
  "MVR",
  "MWK",
  "MXN",
  "MXV",
  "MYR",
  "MZN",
  "NAD",
  "NGN",
  "NIO",
  "NOK",
  "NPR",
  "NZD",
  "OMR",
  "PAB",
  "PEN",
  "PGK",
  "PHP",
  "PKR",
  "PLN",
  "PYG",
  "QAR",
  "RON",
  "RSD",
  "RUB",
  "RWF",
  "SAR",
  "SBD",
  "SCR",
  "SDG",
  "SEK",
  "SGD",
  "SHP",
  "SLL",
  "SOS",
  "SRD",
  "SVC",
  "SYP",
  "SZL",
  "THB",
  "TJS",
  "TMT",
  "TND",
  "TOP",
  "TRY",
  "TTD",
  "TWD",
  "TZS",
  "UAH",
  "UGX",
  "USD",
  "UYI",
  "UYU",
  "UZS",
  "VND",
  "VUV",
  "WST",
  "XAF",
  "XAG",
  "XAU",
  "XBA",
  "XBB",
  "XBC",
  "XBD",
  "XCD",
  "XDR",
  "XOF",
  "XPD",
  "XPF",
  "XPT",
  "XTS",
  "YER",
  "ZAR",
  "ZWL",
] as const;

export type Currencies = keyof typeof PRICE_CURRENCIES;

export interface AmountWithConversions {
  currencyCode: string;
  amount: number;
  precision: number;
  conversions?: { currencyCode: string; amount: number; precision: number }[];
}

export function getAmountByCurrency(
  { conversions, ...amount }: AmountWithConversions,
  currencyCode = "EUR",
): {
  original: Omit<AmountWithConversions, "conversions">;
  converted?: Omit<AmountWithConversions, "conversions"> | undefined;
} {
  if (amount.currencyCode === currencyCode) {
    return { original: amount, converted: undefined };
  }

  return {
    original: amount,
    converted: conversions?.find(({ currencyCode: currency }) => currency === currencyCode),
  };
}

export function getOriginalAmount(amount: number, precision: number): number {
  if (precision > 0) {
    return amount / 10 ** precision;
  }

  return amount;
}

export function getDisplayedAmount(
  amount: AmountWithConversions,
  currencyCode = "EUR",
): { original: string; converted?: string } {
  const { converted, original } = getAmountByCurrency(amount, currencyCode);
  const originalFormat = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: original.currencyCode,
  });

  if (converted) {
    const convertedFormat = new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: converted.currencyCode,
    });

    return {
      original: originalFormat.format(getOriginalAmount(original.amount, original.precision)),
      converted: convertedFormat.format(getOriginalAmount(converted.amount, converted.precision)),
    };
  }

  return {
    original: originalFormat.format(getOriginalAmount(original.amount, original.precision)),
    converted: undefined,
  };
}

export function formatAmount(amount: AmountWithConversions, currencyCode = "EUR"): string {
  const { original, converted } = getDisplayedAmount(amount, currencyCode);

  return converted ? converted : original;
}

export function formatAmountWithConversion(amount: AmountWithConversions, currencyCode = "EUR") {
  const { original, converted } = getDisplayedAmount(amount, currencyCode);

  if (converted) {
    return `${converted} (${original})`;
  }

  return original;
}

/** Cents-based replacement for the dinero.js arithmetic of ClaimRequests.vue. */
export function formatCentsAsEuros(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function applyCommission(cents: number, commissionPercentage: number): number {
  return Math.round((cents * (100 - commissionPercentage)) / 100);
}
